import { renderLinks, wrapWithBdi, getLocalTimezoneSuffix, safeParse } from '../../../../../../Util';
import { Message } from '../../../../types';
import { HideMessageReason, ApplicantIdentity, AudioPathResult } from './types';

// Helper function to generate the hidden message display text
export function generateHiddenMessage(m: Message, L: ReturnType<any>) {
  const metadata = safeParse(m.metadata || '{}');
  if (!metadata.hidden) return '';

  const {
    on,
    reason,
    details,
    attachmentOnly,
  }: {
    on: string;
    reason: HideMessageReason;
    details?: string;
    attachmentOnly?: boolean;
  } = metadata.hidden;

  const descriptions = {
    applicant_request: attachmentOnly
      ? L.applicant.comms.attachments_hidden_applicant_request
      : L.applicant.comms.hidden_applicant_request,
    sensitive_information: attachmentOnly
      ? L.applicant.comms.attachments_hidden_sensitive_info
      : L.applicant.comms.hidden_sensitive_info,
    inappropriate_content: attachmentOnly
      ? L.applicant.comms.attachments_flagged_inappropriate
      : L.applicant.comms.flagged_inappropriate,
    default: attachmentOnly
      ? L.applicant.comms.attachments_hidden
      : L.applicant.comms.message_hidden
  };

  const onDate = on ? ` ${new Date(on).toLocaleString('en-US')} ${getLocalTimezoneSuffix()}` : '';

  const optionalDetails = details ? `: "${details}"` : ''; // not translated because it's a user input

  // Example message format: [Flagged for inappropriate content 10/31/2025, 3:32:05 PM EDT: "Flagged faulty voice recording."]
  return `[${descriptions[reason || 'default']}${onDate}${optionalDetails}]`;
}

// Helper to format message text with phone/link detection
export const prettyTextMessage = (text: string, blankWhatsAppMsg: boolean, L: ReturnType<any>) => {
  if (blankWhatsAppMsg) {
    return <em>{L.applicant.comms.blank_whatsapp_message}</em>;
  }
  if (!text) return <></>;

  // Trim to remove leading/trailing whitespace
  const trimmedText = text.trim();
  if (trimmedText === '--') {
    return <small><em>{L.applicant.comms.media_message}</em></small>;
  }

  const phoneNumberRegex = /((?:\+?\d{1,3}[\s.-]?)?(?:(?:\(\d{3}\))|\d{3})[\s.-]?\d{3}[\s.-]?\d{4})/g;

  // Split by newlines, then process each line for phone numbers and links
  const lines = trimmedText.split('\n');
  return (
    <>
      {lines.map((line, lineIdx) => {
        const wrappedParts = line.split(phoneNumberRegex)
          .filter(x => !!x)
          .map((part, idx) =>
            phoneNumberRegex.test(part)
              ? <span key={`phone-${lineIdx}-${idx}`}>{wrapWithBdi(part)}</span>
              : <span key={`link-${lineIdx}-${idx}`}>{renderLinks(part)}</span>
          );
        return (
          <span key={`line-${lineIdx}`}>
            {wrappedParts}
            {lineIdx < lines.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
};

// Helper function to match contacts (handles +1 prefix)
export function isContactMatch(contact: string, checkAgainst?: string): boolean {
  return !!(checkAgainst && (contact === checkAgainst || contact === '+1' + checkAgainst));
}

// Determine applicant identity from contacts
export function determineApplicantIdentity(
  message: Message,
  applicantInfo: any,
  alternateContacts: any[],
  direction: 'inbound' | 'outbound',
  L: any,
  context: any
): ApplicantIdentity {
  let applicantName: string | undefined;
  let applicantLabel = L.applicant.comms.previous_contact;
  let applicantSource: '' | 'current' | 'alternate' | 'old' = '';
  const metadata = safeParse(message.metadata || '{}');

  // First priority: Check current contacts
  for (let contact of ['phone_number', 'email']) {
    if (isContactMatch(
      direction === 'inbound' ? message.source : message.destination,
      applicantInfo?.[contact]
    )) {
      applicantLabel = contact === 'email' ? L.applicant.comms.current_email : L.applicant.comms.current_phone;
      applicantName = applicantInfo?.['legal_name'];
      applicantSource = 'current';
    }
  }

  // Second priority: Check alternate contacts
  if (applicantLabel === L.applicant.comms.previous_contact || metadata.sentToMultipleEmails) {
    for (const ac of alternateContacts) {
      for (let key of ['phone_key', 'email_key']) {
        let contactIndex = -1;
        for (let contactValue of (applicantInfo?.[ac[key]] || '').split(',')) {
          contactIndex++;
          let oneoffIsMatch = isContactMatch(
            direction === 'inbound' ? message.source : message.destination,
            contactValue
          );
          let multipleIsMatch = message.destination.split(',').some(dest =>
            isContactMatch(dest, contactValue)
          );
          if (oneoffIsMatch || multipleIsMatch) {
            applicantName = applicantInfo?.[ac.name_key] || L.applicant.comms.alternate_contact;
            applicantLabel = ac.label[context.lang] || ac.label['en'];
            applicantSource = 'alternate';
          }
          // Handle comma-separated names
          if (!multipleIsMatch && oneoffIsMatch) {
            applicantName = (applicantInfo?.[ac.name_key]?.split(',')[contactIndex])?.trim() || applicantName;
          }
        }
      }
    }
  }

  // Fallback to legal_name or source
  if (!applicantName) {
    applicantName = applicantInfo?.['legal_name'] || message.source;
  }

  return {
    name: applicantName || '',
    label: applicantLabel,
    source: applicantSource
  };
}

// Detect audio path from various metadata fields
export function getAudioPath(metadata: any): AudioPathResult {
  let audioPath = null;
  let needsSignedUrl = false;

  // First check for direct URLs (RecordingUrl, MediaUrl0)
  if (metadata.RecordingUrl && metadata.RecordingUrl.trim()) {
    audioPath = metadata.RecordingUrl;
  } else if (metadata.MediaUrl0 && metadata.MediaUrl0.includes('audio')) {
    audioPath = metadata.MediaUrl0;
  }
  // Check if there's an audio attachment in metadata.attachments array
  else if (metadata.attachments && Array.isArray(metadata.attachments)) {
    const attachmentTypes = metadata.attachmentTypes || [];
    for (let i = 0; i < metadata.attachments.length; i++) {
      const attachmentType = attachmentTypes[i] || '';
      if (attachmentType.includes('audio')) {
        audioPath = metadata.attachments[i];
        needsSignedUrl = true; // Attachments need to be converted to signed URLs
        break;
      }
    }
  }

  return { path: audioPath, needsSignedUrl };
}

// Check if attachment is an image
export function isImageAttachment(attachmentType: string, url: string): boolean {
  return attachmentType.includes('image') ||
    url.toLowerCase().match(/\.(png|jpg|jpeg|gif|webp)($|\?)/) !== null;
}

// Extract filename from attachment path
export function getAttachmentFilename(path: string): string {
  return path.split('/').pop() || 'Download attachment';
}
